import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppointmentActions } from '../../store/appointments/appointment.actions';
import {
  selectAppointmentError,
  selectAppointmentSubmitted,
  selectAppointmentsSaving,
} from '../../store/appointments/appointment.selectors';

const contactMethodValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const email = String(control.get('email')?.value || '').trim();
  const phone = String(control.get('phone')?.value || '').trim();
  return email || phone ? null : { contactMethodRequired: true };
};

const phonePattern = /^\s*(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}\s*$/;

@Component({
  selector: 'atr-booking',
  standalone: true,
  imports: [AsyncPipe, ReactiveFormsModule],
  template: `
    <main class="page-shell">
      <section class="page-hero compact">
        <p class="section-kicker">Get in touch</p>
        <h1>Personalized detailing starts here.</h1>
        <p>Call me or send a few details about your vehicle, and I’ll help you figure out the next step.</p>
      </section>

      <section class="booking section page-booking">
        <div class="booking-copy">
          <p class="section-kicker">Contact me</p>
          <h1>Tell me what your vehicle needs.</h1>
          <p>I’ll learn what your vehicle needs, answer your questions, and provide pricing before scheduling.</p>
          <div class="contact-callout">
            <strong>Prefer to talk it over?</strong>
            <p>Give me a call. I’m happy to answer questions, discuss what your vehicle needs, and find a time that works.</p>
            <a class="button primary" href="tel:+13093355423">Call 309-335-5423</a>
          </div>
          <div class="contact-lines">
            <a href="mailto:atr2982@gmail.com">atr2982@gmail.com</a>
            <span>529 W Main Street, Bushnell, IL 61422</span>
          </div>
        </div>

        <form class="booking-form" [formGroup]="form" (ngSubmit)="submit()">
          <label class="full">Name<input formControlName="name" type="text" autocomplete="name" /></label>
          <label>Email<input formControlName="email" type="email" autocomplete="email" /></label>
          <label>Phone<input formControlName="phone" type="tel" autocomplete="tel" /></label>
          <label class="full">Vehicle<input formControlName="vehicle" type="text" placeholder="Year, make, model" /></label>
          <label>What are you looking for?
            <select formControlName="service">
              <option>Exterior detail</option><option>Interior detail</option><option>Full detail</option>
            </select>
          </label>
          <fieldset class="add-on-fieldset full"><legend>Anything extra?</legend>
            <div class="add-on-grid">
              @for (addOn of addOnOptions; track addOn) {
                <label class="checkbox-row"><input type="checkbox" [checked]="isAddOnSelected(addOn)" (change)="toggleAddOn(addOn, $any($event.target).checked)" /><span>{{ addOn }}</span></label>
              }
            </div>
          </fieldset>
          <label class="checkbox-row full"><input formControlName="mobileDetail" type="checkbox" /><span>I'm interested in a mobile detail</span></label>
          @if (form.controls.mobileDetail.value) {
            <div class="mobile-detail-fields full">
              <p class="booking-note">Mobile details are available when the location has water and electricity. I’ll include any mobile fee in the quote.</p>
              <label>Mobile address<input formControlName="mobileAddress" type="text" placeholder="Where the vehicle will be detailed" /></label>
              <label class="checkbox-row"><input formControlName="waterElectricityAvailable" type="checkbox" /><span>Water and electricity are available at this location</span></label>
            </div>
          }
          <label class="full">Notes<textarea formControlName="message" rows="5" placeholder="Tell me about the vehicle condition, timing, or any problem areas."></textarea></label>
          <label class="full photo-upload">Vehicle photos (optional)
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple (change)="selectPhotos($event)" />
            <span class="photo-help">Add up to 5 photos of the interior, exterior, or areas that need attention.</span>
          </label>
          <p class="booking-note full">You’ll hear back from me within 1 business day with pricing and scheduling options.</p>
          @if (error$ | async; as error) { <p class="form-status error full">{{ error }}</p> }
          @if (submitted$ | async) { <p class="form-status success full">Thanks for reaching out. I’ll review the details and get back to you with pricing and next steps.</p> }
          @if (form.controls.phone.hasError('pattern') && form.controls.phone.touched) {
            <p class="form-status error full">Please enter a valid phone number.</p>
          } @else if (form.controls.email.hasError('email') && form.controls.email.touched) {
            <p class="form-status error full">Please enter a valid email address.</p>
          } @else if (form.hasError('contactMethodRequired') && (form.controls.email.touched || form.controls.phone.touched)) {
            <p class="form-status error full">Please provide an email address or phone number.</p>
          }
          <button class="button primary full" type="submit" [disabled]="form.invalid || (saving$ | async)">{{ (saving$ | async) ? 'Sending...' : 'Send details for a call back' }}</button>
        </form>
      </section>
    </main>
  `,
})
export class BookingComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  protected readonly saving$ = this.store.select(selectAppointmentsSaving);
  protected readonly submitted$ = this.store.select(selectAppointmentSubmitted);
  protected readonly error$ = this.store.select(selectAppointmentError);
  private readonly subscription = new Subscription();
  protected selectedPhotos: File[] = [];
  protected readonly addOnOptions = ['Paint correction', 'Tar removal', 'Carpet shampoo', 'Pet hair removal', 'Headlight restoration'];

  protected readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required], email: ['', Validators.email], phone: ['', Validators.pattern(phonePattern)],
    vehicle: ['', Validators.required], service: ['Exterior detail', Validators.required], addOns: [[] as string[]],
    mobileDetail: [false], mobileAddress: [''], waterElectricityAvailable: [false], message: [''],
  }, { validators: contactMethodValidator });

  ngOnInit(): void {
    this.store.dispatch(AppointmentActions.clearSubmissionState());
    this.subscription.add(this.submitted$.subscribe((submitted) => {
      if (submitted) {
        this.form.reset({ name: '', email: '', phone: '', vehicle: '', service: 'Exterior detail', addOns: [], mobileDetail: false, mobileAddress: '', waterElectricityAvailable: false, message: '' });
        this.selectedPhotos = [];
      }
    }));
  }

  ngOnDestroy(): void { this.subscription.unsubscribe(); }

  protected submit(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.store.dispatch(AppointmentActions.createAppointment({ request: { ...this.form.getRawValue(), photos: this.selectedPhotos } }));
  }

  protected selectPhotos(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedPhotos = Array.from(input.files || [])
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, 5);
  }

  protected isAddOnSelected(addOn: string): boolean { return this.form.controls.addOns.value.includes(addOn); }

  protected toggleAddOn(addOn: string, checked: boolean): void {
    const current = this.form.controls.addOns.value;
    this.form.controls.addOns.setValue(checked ? [...current, addOn] : current.filter((item) => item !== addOn));
  }
}
